import React, { useEffect } from "react";
import TableCtsm from '../../../../TableCstm/TableCstm';
import './PaymentHistory.scss'
import { getLocalDateObjectFromInput } from "../../../../../utils/dateTimeZone";
const PaymentHistory = ({ student, paymentPlan }) => {
    const { paymentHistory = [] } = student;

    if (!paymentHistory || paymentHistory.length === 0) {
        return null;
    }

    const formatDate = (date) => {
        let localDate;
        if (typeof date === 'string') {
            localDate = getLocalDateObjectFromInput(date);
        } else {
            localDate = date;
        }
        const day = String(localDate.getDate()).padStart(2, '0');
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const year = localDate.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Función para calcular el rango de fechas de una semana
    const getWeekDateRange = (weekNumber, year) => {
        // Obtener el 4 de enero del año dado (para cumplir con la norma ISO 8601)
        const fourthOfJanuary = new Date(year, 0, 4);

        // Calcular el lunes de la primera semana ISO 8601 (restando los días necesarios para llegar al lunes)
        const firstMonday = new Date(fourthOfJanuary);
        firstMonday.setDate(fourthOfJanuary.getDate() - (fourthOfJanuary.getDay() + 6) % 7);
        // Calcular el inicio de la semana deseada sumando (weekNumber - 1) * 7 días
        const startOfWeek = new Date(firstMonday);
        startOfWeek.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);

        // Calcular el final de la semana (sumando 6 días al inicio)
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        return { start: startOfWeek, end: endOfWeek };
    };

    // Función para combinar semanas consecutivas
    const combineConsecutiveWeeks = (weeks) => {
        let combined = [];
        let currentRange = null;

        weeks.forEach((week, index) => {
            const [weekNumber, year] = week.split("-");
            if (!weekNumber || !year) return; // Verificar si weekNumber o year están vacíos

            const { start, end } = getWeekDateRange(Number(weekNumber), Number(year));

            if (!start || !end) return; // Verificar si las fechas de inicio y fin son válidas

            if (currentRange === null) {
                currentRange = { start, end };
            } else {
                const prevEnd = currentRange.end;
                if (start.getTime() === prevEnd.getTime() + 86400000) { // Comprobar si son consecutivas
                    currentRange.end = end; // Extender el rango
                } else {
                    combined.push(`${formatDate(currentRange.start)} - ${formatDate(currentRange.end)}`);
                    currentRange = { start, end }; // Iniciar un nuevo rango
                }
            }
        });

        if (currentRange !== null) {
            combined.push(`${formatDate(currentRange.start)} - ${formatDate(currentRange.end)}`);
        }

        return combined.join(", ");
    };


    const columns = [
        { header: "Fecha", accessor: "date", className: "tableCtype3 td" },
        { header: "Concepto", accessor: "concept", className: "tableCtype2 td" },
        { header: "Semanas", accessor: "weeksPaid", className: "tableCtype1 td" },
        { header: "", accessor: "dateRange", className: "tableCtype5 td" },
        { header: "Monto", accessor: "amount", className: "tableCtype3 td" },
        { header: "Porcentaje pagado colegiatura", accessor: "percent_paid", className: "tableCtype5 td" },
    ];
    //Function to split weeks paid fully and partial pays
    const filterWeeks = (payment) => {
        // filter fully paid weeks
        const fullyPaidWeeks = payment.payedWeeks
            .filter(week => {
                return parseInt(week.percent_paid) === 100;  // Compara como número
            });

        // filter week partial pays
        const partialPays = payment.payedWeeks
            .filter(week => {
                return parseInt(week.percent_paid) !== 100;  // Compara como número
            });

        //Format the dates 
        const fullyPaidWeeksStr = fullyPaidWeeks.length > 0
            ? combineConsecutiveWeeks(fullyPaidWeeks.map(week => week.week))
            : "-"

        const partialPaysStr = partialPays.length > 0
            ? combineConsecutiveWeeks(partialPays.map(week => week.week)) : "-"

        return (
            <div className="week-range">
                <p className="fully-paid">{fullyPaidWeeksStr}</p>
                {partialPays.length > 0 && (
                    <p className="partial-pay">{partialPaysStr}</p>
                )}
            </div>
        );
    }
    const splitConcepts = (concepts = {}) => {
        return (
            <div className="concepts">
                {Object.entries(concepts).map(([concept, percent], index) => {
                    const numericPercent = parseFloat(percent.replace('%', ''));
                    const isPartial = numericPercent < 100;

                    return (
                        <span
                            key={index}
                            className={`concept-item ${isPartial ? 'partial-pay' : ''}`}
                        >
                            {`${concept}, `}
                        </span>
                    );
                })}
            </div>
        );
    };
    function roundToNearestHalf(value) {
        return Math.round(value * 2) / 2;
    }
    //Function to get de week number of a date
    function getWeekNumber(date) {
        const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Ajustar para que la semana inicie en lunes
        tempDate.setUTCDate(tempDate.getUTCDate() + 4 - (tempDate.getUTCDay() || 7));
        // Calcular el inicio del año
        const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
        // Calcular la diferencia en días y obtener la semana
        const weekNumber = Math.ceil(((tempDate - yearStart) / 86400000 + 1) / 7);

        return { weekNumber, year: tempDate.getUTCFullYear() };
    }
    const splitPercents = (
        conceptPercentages,
        amountStr,
        fullHistory = [],
        currentReceiptId = null,
        payedWeeks = [],
    ) => {
        if (!conceptPercentages) return ["-"];

        const amounts = amountStr?.split(',').map(a => a.trim()) || [];
        const currentWeek = payedWeeks?.[0]?.week; // e.g.,
        const [weekNum, year] = currentWeek ? currentWeek.split('-').map(Number) : [];

        const todayInfo = getWeekNumber(new Date());
        const today = new Date();
        const dayIndex = today.getDay() === 0 ? 7 : today.getDay(); // domingo = 7
        const isPromptDay = dayIndex >= 1 && dayIndex <= 3;

        const percentJSX = (
            <div className="percents">
                {Object.entries(conceptPercentages).map(([concept, percent], index) => {
                    const amount = amounts[index] ? parseFloat(amounts[index]) : null;
                    const percentNumber = parseFloat(percent);

                    let extraInfo = '';

                    if (concept === 'Abono' && percentNumber < 100 && amount !== null) {
                        let totalAmount = amount;

                        fullHistory.forEach(entry => {
                            if (
                                entry.receipt_id !== currentReceiptId &&
                                entry.concept.includes('Abono') &&
                                entry.payedWeeks?.some(w => w.week === currentWeek)
                            ) {
                                const entryAmounts = entry.amount?.split(',').map(a => a.trim()) || [];
                                const indexAbono = Object.keys(entry.conceptPercentages).findIndex(k => k === 'Abono');
                                const prevAmount = entryAmounts[indexAbono] ? parseFloat(entryAmounts[indexAbono]) : 0;
                                totalAmount += prevAmount;
                            }
                        });

                        let totalToPay = null;
                        if (paymentPlan) {
                            if (todayInfo.weekNumber > weekNum) {
                                totalToPay = paymentPlan.late;
                            } else if (todayInfo.weekNumber === weekNum) {
                                totalToPay = isPromptDay ? paymentPlan.prompt : paymentPlan.normal;
                            } else {
                                totalToPay = (100 * totalAmount) / percentNumber;
                            }
                        } else {
                            totalToPay = (100 * totalAmount) / percentNumber;
                        }

                        const remaining = roundToNearestHalf(totalToPay - totalAmount);
                        extraInfo = ` - Restante por pagar: $${remaining}`;
                    }

                    return (
                        <span
                            key={index}
                            className={`${percentNumber < 100 ? 'partial-pay' : ''}`}
                        >
                            {`${concept}: ${percent} ($${amount})`}
                            <div className="partialpay-remaining">
                                {percentNumber < 100 ? `${extraInfo}` : 'COMPLETADO'}
                            </div>
                        </span>
                    );
                })}
            </div>
        );

        return percentJSX;
    };

    // Ordenar por fecha de recibo descendente (más reciente primero)
    const sortedHistory = [...paymentHistory].sort((a, b) => {
        const dateA = a.receipt_date.length === 10
            ? getLocalDateObjectFromInput(a.receipt_date)
            : new Date(a.receipt_date);
        const dateB = b.receipt_date.length === 10
            ? getLocalDateObjectFromInput(b.receipt_date)
            : new Date(b.receipt_date);
        return dateB - dateA; // Descendente
    });

    //Table data 
    const data = sortedHistory.map(payment => {
        const dateRange = filterWeeks(payment);
        const concepts = splitConcepts(payment.conceptPercentages);
        const percents = splitPercents(payment.conceptPercentages, payment.amount, sortedHistory,
            payment.receipt_id, payment.payedWeeks
        );
        const weeksPaid = Array.isArray(payment.payedWeeks)
            ? payment.payedWeeks.filter(w => w.week !== "-" && w.percent_paid !== "").length
            : 0;
        return {
            date: payment.receipt_date || "Fecha no proporcionada",
            concept: concepts || "Concepto no proporcionado",
            dateRange,
            amount: payment.amount ? `$ ${payment.amount}` : "$ 0.00",
            weeksPaid,
            percent_paid: percents,
        };
    });
    return (
        <div className="payment-history-contain scroll">
            <h1 className="title-2">Historial de pago</h1>
            <TableCtsm columns={columns} data={data} className={'payment-history-table'} />
        </div>
    );
};

export default PaymentHistory;
